import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	IconButton,
	Box,
	CircularProgress,
	Typography,
	SvgIcon,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import XIcon from '@untitled-ui/icons-react/build/esm/X'
import ReactMarkdown from 'react-markdown'

/**
 * Parses the entire CHANGELOG markdown into sections.
 * Each section starts with a "## " header and continues until the next "## " or EOF.
 */
const parseChangelog = (markdown) => {
	if (!markdown) return []
	const md = markdown.replace(/\r\n/g, '\n')

	// Find all headers
	const headerRegex = /^##\s+.*$/gm
	const headers = [...md.matchAll(headerRegex)]

	// If no section headers, return single section with whole file
	if (headers.length === 0) {
		return [
			{
				id: 0,
				header: 'Changelog',
				version: null,
				date: null,
				content: md.trim(),
			},
		]
	}

	const sections = []
	for (let i = 0; i < headers.length; i++) {
		const start = headers[i].index
		const end = i + 1 < headers.length ? headers[i + 1].index : md.length
		const raw = md.slice(start, end).trim()

		// Extract header line and body
		const firstLineBreak = raw.indexOf('\n')
		const headerLine = firstLineBreak === -1 ? raw : raw.slice(0, firstLineBreak)
		const body = firstLineBreak === -1 ? '' : raw.slice(firstLineBreak + 1).trim()

		// Try to extract version and optional date of format "## x.y.z — yyyy-mm-dd"
		const m = headerLine.match(/^##\s+(.+?)(?:\s+—\s+(\d{4}-\d{2}-\d{2}))?\s*$/)
		const version = m ? m[1] : headerLine.replace(/^##\s+/, '').trim()
		const date = m ? m[2] || null : null

		sections.push({
			id: i,
			header: headerLine.replace(/^##\s+/, '').trim(),
			version,
			date,
			content: body,
		})
	}

	return sections
}

export const ChangelogDialog = ({ open, onClose, version }) => {
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')
	const [markdown, setMarkdown] = useState('')
	const [expanded, setExpanded] = useState(() => new Set())

	const load = useCallback(async () => {
		setError('')
		setLoading(true)
		try {
			// Served from public/CHANGELOG.md
			const res = await fetch('/CHANGELOG.md', {
				cache: 'no-store',
			})
			if (!res.ok) throw new Error(`Failed to load changelog (${res.status})`)
			const text = await res.text()
			setMarkdown(text)
		} catch (e) {
			setError(e?.message || 'Failed to load changelog')
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		if (open) {
			load()
		}
	}, [open, load])

	const sections = useMemo(() => parseChangelog(markdown), [markdown])

	// Open latest by default (prefer the section that matches the provided version, otherwise the first)
	useEffect(() => {
		if (!sections.length) return
		let initialIndex = 0
		if (version) {
			const matchIdx = sections.findIndex((s) => s.version === version || s.header.includes(version))
			if (matchIdx !== -1) initialIndex = matchIdx
		}
		setExpanded(new Set([initialIndex]))
	}, [sections, version])

	const toggle = (id) => {
		setExpanded((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	const latestVersionLabel = useMemo(() => {
		if (!sections.length) return version
		return sections[0]?.version || sections[0]?.header || version
	}, [sections, version])

	return (
		<Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
			<DialogTitle sx={{ pr: 6 }}>
				<Typography variant="h6">Changelog</Typography>
				{latestVersionLabel ? (
					<Typography variant="body2" color="text.secondary">
						Latest: {latestVersionLabel}
					</Typography>
				) : null}
				<IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
					<SvgIcon>
						<XIcon />
					</SvgIcon>
				</IconButton>
			</DialogTitle>
			<DialogContent dividers sx={{ p: 0 }}>
				{loading ? (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, px: 3 }}>
						<CircularProgress size={20} />
						<Typography variant="body2">Loading changelog…</Typography>
					</Box>
				) : error ? (
					<Box sx={{ py: 3, px: 3 }}>
						<Typography color="error" variant="body2">
							{error}
						</Typography>
					</Box>
				) : sections.length === 0 ? (
					<Box sx={{ py: 3, px: 3 }}>
						<Typography variant="body2">No changelog entries found.</Typography>
					</Box>
				) : (
					// Scrollable list of versions
					<Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
						{sections.map((sec) => (
							<Accordion
								key={sec.id}
								expanded={expanded.has(sec.id)}
								onChange={() => toggle(sec.id)}
								disableGutters
								sx={{
									'&:before': { display: 'none' },
									boxShadow: 'none',
									borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
								}}
							>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
										<Typography variant="subtitle1" fontWeight={600}>
											{sec.version || sec.header}
										</Typography>
										{sec.date ? (
											<Typography variant="caption" color="text.secondary">
												{sec.date}
											</Typography>
										) : null}
									</Box>
								</AccordionSummary>
								<AccordionDetails>
									<Box sx={{ '& h2': { mt: 0 }, '& ul': { pl: 3 }, '& p': { mb: 1.5 } }}>
										<ReactMarkdown>{sec.content}</ReactMarkdown>
									</Box>
								</AccordionDetails>
							</Accordion>
						))}
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} variant="contained">
					Close
				</Button>
			</DialogActions>
		</Dialog>
	)
}

ChangelogDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	version: PropTypes.string.isRequired,
}
